// ──────────────────────────────────────────────────────────────────────
// D59 + D84 — "Live / Last synced N s ago" pill.
//
// D84 update: pill colour now reflects the workspace WebSocket state:
//   green  → live      (WS connected, real-time push active)
//   amber  → polling   (WS soft-failed; falling back to 15s polling)
//   amber  → reconnecting
//   grey   → disconnected (no workspace / pre-login / explicit disconnect)
// The "N s ago" text comes from the local `lastSynced` value the host
// widget owns (same as before — caller passes it in).
// ──────────────────────────────────────────────────────────────────────
import React, { useEffect, useState } from 'react';
import { Loader2, RefreshCw, Wifi, WifiOff } from 'lucide-react';
import { useWorkspaceLive } from '../lib/WorkspaceLiveProvider';

function formatAgo(seconds) {
  if (seconds < 5) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  const mins = Math.floor(seconds / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ago`;
}

const STATE_STYLES = {
  live: {
    cls: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/15',
    Icon: Wifi,
    label: 'Live',
    title: 'Real-time sync active',
  },
  reconnecting: {
    cls: 'bg-amber-500/10 border-amber-500/30 text-amber-300 hover:bg-amber-500/20',
    Icon: RefreshCw,
    label: 'Reconnecting',
    title: 'Reconnecting to live sync — polling meanwhile',
  },
  polling: {
    cls: 'bg-amber-500/10 border-amber-500/30 text-amber-300 hover:bg-amber-500/20',
    Icon: RefreshCw,
    label: 'Polling',
    title: 'Live sync unavailable — polling every 15s. Click to retry.',
  },
  disconnected: {
    cls: 'bg-foreground/[0.05] border-border/30 text-muted-foreground hover:bg-foreground/[0.08]',
    Icon: WifiOff,
    label: 'Offline',
    title: 'Workspace sync not active',
  },
};

export default function LastSyncedPill({ lastSynced, loading, onRefetch, label }) {
  const [tick, setTick] = useState(0);
  const { connectionState, forceReconnect } = useWorkspaceLive();
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const ago = lastSynced
    ? Math.max(0, Math.floor((Date.now() - lastSynced.getTime()) / 1000))
    : null;
  const style = STATE_STYLES[connectionState] || STATE_STYLES.disconnected;
  const Icon = loading ? Loader2 : style.Icon;
  const displayLabel = label || style.label;
  // Clicking the pill always refetches local data; if the WS has soft-
  // failed it ALSO forces a reconnect attempt. This gives users a
  // single-click "kick" when they suspect they're seeing stale data.
  const onClick = () => {
    try { onRefetch?.(); } catch { /* noop */ }
    if (connectionState === 'polling' || connectionState === 'disconnected') {
      try { forceReconnect?.(); } catch { /* noop */ }
    }
  };

  return (
    <button
      type="button"
      onClick={onClick}
      title={lastSynced ? `${style.title} — last refresh ${lastSynced.toLocaleTimeString()}` : style.title}
      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] uppercase tracking-wide border transition-colors ${style.cls}`}
      data-testid="last-synced-pill"
      data-connection-state={connectionState}
      data-tick={tick}
    >
      <Icon className={`h-3 w-3 ${loading || connectionState === 'reconnecting' ? 'animate-spin' : ''}`} />
      <span className="font-medium">{displayLabel}</span>
      {ago != null && <span className="text-[9px] opacity-60">· {formatAgo(ago)}</span>}
    </button>
  );
}
